import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Save, ClipboardEdit, MapPin, CalendarDays, Pencil, X, Store, FileDown, Copy } from "lucide-react";
import { useAuth, useCabang } from "@/context/AuthContext";
import { formatIDR, todayISO } from "@/lib/format";
import { generateReportPDF } from "@/lib/reportPdf";

const CATS = [
  {
    key: "bahan_baku", label: "Bahan Baku", accent: "border-blue-200 bg-blue-50/40", path: "bahan-baku",
    selisih: { from: "anggaran", to: "pembelian" },
    columns: [
      { name: "item", label: "Item Barang", type: "text", required: true, w: "min-w-[160px]" },
      { name: "jumlah", label: "Jumlah", type: "number", w: "w-24" },
      { name: "anggaran", label: "Penjualan (Rp)", type: "number", money: true, w: "w-44" },
      { name: "pembelian", label: "Pembelian (Rp)", type: "number", money: true, w: "w-44" },
      { name: "catatan", label: "Catatan", type: "text", w: "min-w-[140px]" },
    ],
  },
  {
    key: "ops", label: "Operasional", accent: "border-amber-200 bg-amber-50/40", path: "ops",
    selisih: { from: "anggaran", to: "pembelian" },
    columns: [
      { name: "item", label: "Item", type: "text", required: true, w: "min-w-[160px]" },
      { name: "jumlah", label: "Jumlah", type: "number", w: "w-24" },
      { name: "anggaran", label: "Penjualan (Rp)", type: "number", money: true, w: "w-44" },
      { name: "pembelian", label: "Pembelian (Rp)", type: "number", money: true, w: "w-44" },
      { name: "catatan", label: "Catatan", type: "text", w: "min-w-[140px]" },
    ],
  },
  {
    key: "insentif", label: "Insentif Usaha", accent: "border-emerald-200 bg-emerald-50/40", path: "insentif",
    selisih: { from: "anggaran", to: "realisasi" },
    columns: [
      { name: "nama", label: "Nama", type: "text", required: true, w: "min-w-[160px]" },
      { name: "anggaran", label: "Penjualan (Rp)", type: "number", money: true, w: "w-44" },
      { name: "realisasi", label: "Realisasi (Rp)", type: "number", money: true, w: "w-44" },
      { name: "catatan", label: "Catatan", type: "text", w: "min-w-[140px]" },
    ],
  },
  {
    key: "lainnya", label: "Lainnya", accent: "border-slate-200 bg-slate-50/40", path: "lainnya",
    columns: [
      { name: "item", label: "Item", type: "text", required: true, w: "min-w-[160px]" },
      { name: "pembelian", label: "Pembelian (Rp)", type: "number", money: true, w: "w-44" },
      { name: "catatan", label: "Catatan", type: "text", w: "min-w-[140px]" },
    ],
  },
  {
    key: "setoran", label: "Setoran Kasir (Pemasukan Riil)", accent: "border-teal-200 bg-teal-50/40", path: "setoran",
    columns: [
      { name: "sumber", label: "Sumber", type: "text", required: true, w: "min-w-[160px]" },
      { name: "metode", label: "Metode (cash/transfer/qris)", type: "text", w: "w-40" },
      { name: "nominal", label: "Nominal (Rp)", type: "number", money: true, w: "w-44" },
      { name: "catatan", label: "Catatan", type: "text", w: "min-w-[140px]" },
    ],
  },
];

function MoneyInput({ value, onChange, testid, required }) {
  const num = Number(value);
  const display = value === "" || value === null || value === undefined || Number.isNaN(num) || num === 0
    ? (value === 0 || value === "0" ? "Rp 0" : "")
    : `Rp ${new Intl.NumberFormat("id-ID").format(num)}`;
  return (
    <Input type="text" inputMode="numeric" value={display}
           onChange={(e) => {
             const digits = e.target.value.replace(/[^\d]/g, "");
             onChange(digits === "" ? "" : Number(digits));
           }}
           placeholder="Rp 0"
           className="h-9 font-mono text-right" required={required} data-testid={testid} />
  );
}

const selisihValue = (cat, row) => {
  if (!cat.selisih) return null;
  const a = Number(row[cat.selisih.from] || 0);
  const b = Number(row[cat.selisih.to] || 0);
  return a - b;
};

// Datalist untuk item suggestion (cached per kategori)
const _suggestCache = {};
function ItemDatalist({ id, kategori }) {
  const [items, setItems] = useState(_suggestCache[kategori] || []);
  useEffect(() => {
    if (_suggestCache[kategori]) { setItems(_suggestCache[kategori]); return; }
    api.get("/items/suggest", { params: { kategori } })
      .then(({ data }) => { _suggestCache[kategori] = data.items || []; setItems(_suggestCache[kategori]); })
      .catch(() => {});
  }, [kategori]);
  return (
    <datalist id={id}>
      {items.map((it) => <option key={it} value={it} />)}
    </datalist>
  );
}

const emptyRow = (cat) => Object.fromEntries(cat.columns.map((c) => [c.name, c.type === "number" ? 0 : ""]));

const emptyEdits = () => Object.fromEntries(CATS.map((c) => [c.key, {}]));
const emptyDrafts = () => Object.fromEntries(CATS.map((c) => [c.key, []]));

export default function InputHarian() {
  const { user } = useAuth();
  const { cabangList } = useCabang();
  const isCabangUser = user?.role === "cabang";
  const isSuper = user?.role === "super_admin";
  const isViewer = user?.role === "viewer";
  const navigate = useNavigate();

  const [adminId, setAdminId] = useState("");
  const [admins, setAdmins] = useState([]);
  const [cabangId, setCabangId] = useState(user?.cabang_id || "");
  const [tanggal, setTanggal] = useState(todayISO());
  const [searchParams] = useSearchParams();

  // Preload dari URL query (dipakai saat navigasi dari halaman Riwayat)
  useEffect(() => {
    const qcbg = searchParams.get("cabang");
    const qtgl = searchParams.get("tanggal");
    if (qcbg) setCabangId(qcbg);
    if (qtgl) setTanggal(qtgl);
    // eslint-disable-next-line
  }, []);
  const [existing, setExisting] = useState(null);
  const [drafts, setDrafts] = useState(emptyDrafts);
  const [edits, setEdits] = useState(emptyEdits);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isCabangUser && user?.cabang_id) setCabangId(user.cabang_id);
  }, [isCabangUser, user?.cabang_id]);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" && (u.workspace_id === user?.active_workspace_id || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  // Cabang yang bisa dipilih berdasarkan Usaha (untuk super_admin)
  const visibleCabang = useMemo(() => {
    if (!isSuper) return cabangList;
    if (!adminId) return [];
    return cabangList.filter((c) => c.admin_id === adminId);
  }, [cabangList, isSuper, adminId]);

  // Reset cabang saat Usaha diubah dan cabang lama tidak lagi tersedia
  useEffect(() => {
    if (isSuper && cabangId && !visibleCabang.some((c) => c.id === cabangId)) {
      setCabangId("");
    }
  }, [isSuper, visibleCabang, cabangId]);

  const load = useCallback(async () => {
    if (!cabangId || !tanggal) { setExisting(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/input-harian", { params: { cabang_id: cabangId, tanggal } });
      setExisting(data);
      setEdits(emptyEdits());   // reset editing when cabang/tanggal changed
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat");
      setExisting(null);
    } finally { setLoading(false); }
  }, [cabangId, tanggal]);

  useEffect(() => { load(); }, [load]);

  const addDraftRow = (key) => {
    const cat = CATS.find((c) => c.key === key);
    setDrafts((d) => ({ ...d, [key]: [...d[key], emptyRow(cat)] }));
  };
  const removeDraftRow = (key, idx) => setDrafts((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));
  const updateDraft = (key, idx, field, val) => {
    setDrafts((d) => ({ ...d, [key]: d[key].map((r, i) => i === idx ? { ...r, [field]: val } : r) }));
  };

  const startEdit = (key, row) => {
    const cat = CATS.find((c) => c.key === key);
    const patch = Object.fromEntries(cat.columns.map((c) => [c.name, row[c.name] ?? (c.type === "number" ? 0 : "")]));
    setEdits((e) => ({ ...e, [key]: { ...e[key], [row.id]: patch } }));
  };
  const cancelEdit = (key, rowId) => {
    setEdits((e) => {
      const copy = { ...e[key] }; delete copy[rowId];
      return { ...e, [key]: copy };
    });
  };
  const updateEdit = (key, rowId, field, val) => {
    setEdits((e) => ({ ...e, [key]: { ...e[key], [rowId]: { ...e[key][rowId], [field]: val } } }));
  };

  const totalDrafts = useMemo(() => Object.values(drafts).reduce((s, arr) => s + arr.length, 0), [drafts]);
  const totalEdits = useMemo(() => Object.values(edits).reduce((s, obj) => s + Object.keys(obj).length, 0), [edits]);
  const totalPending = totalDrafts + totalEdits;

  const validateRow = (cat, row) => {
    for (const col of cat.columns) {
      if (col.required && !String(row[col.name] ?? "").trim()) {
        toast.error(`${cat.label}: kolom ${col.label} wajib diisi`);
        return false;
      }
    }
    return true;
  };

  const saveAll = async () => {
    if (!cabangId) { toast.error("Pilih lokasi cabang"); return; }
    if (!tanggal) { toast.error("Pilih tanggal"); return; }
    if (totalPending === 0) { toast.info("Belum ada perubahan untuk disimpan"); return; }
    for (const cat of CATS) {
      for (const row of drafts[cat.key]) if (!validateRow(cat, row)) return;
      for (const [, row] of Object.entries(edits[cat.key])) if (!validateRow(cat, row)) return;
    }
    setSaving(true);
    try {
      // 1) Bulk POST drafts
      if (totalDrafts > 0) {
        const payload = { cabang_id: cabangId, tanggal };
        for (const cat of CATS) {
          payload[cat.key] = drafts[cat.key].map((r) => {
            const out = { ...r };
            for (const col of cat.columns) if (col.type === "number") out[col.name] = Number(out[col.name] || 0);
            return out;
          });
        }
        await api.post("/input-harian", payload);
      }
      // 2) PUT each edited row (existing rows include cabang_id + tanggal from prior state)
      for (const cat of CATS) {
        for (const [rowId, patch] of Object.entries(edits[cat.key])) {
          const original = (existing?.[cat.key] || []).find((r) => r.id === rowId);
          const body = { ...(original || {}), ...patch, tanggal, cabang_id: cabangId };
          for (const col of cat.columns) if (col.type === "number") body[col.name] = Number(body[col.name] || 0);
          delete body.id; delete body.created_by; delete body.created_at;
          await api.put(`/${cat.path}/${rowId}`, body);
        }
      }
      toast.success(`${totalPending} perubahan tersimpan`);
      setDrafts(emptyDrafts());
      setEdits(emptyEdits());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const deleteExisting = async (cat, row) => {
    // Optimistic delete + Undo toast
    setExisting((prev) => prev ? { ...prev, [cat.key]: prev[cat.key].filter((r) => r.id !== row.id) } : prev);
    let undone = false;
    toast("Baris dihapus", {
      action: { label: "Undo", onClick: () => { undone = true; } },
      duration: 5000,
    });
    await new Promise((r) => setTimeout(r, 5000));
    if (undone) { toast.success("Dibatalkan"); load(); return; }
    try { await api.delete(`/${cat.path}/${row.id}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); load(); }
  };

  const [exporting, setExporting] = useState(false);
  const [copying, setCopying] = useState(false);

  const copyFromLast = async () => {
    if (!cabangId || !tanggal) { toast.error("Pilih cabang & tanggal dulu"); return; }
    setCopying(true);
    try {
      const { data } = await api.get("/input-harian/last", { params: { cabang_id: cabangId, before: tanggal } });
      if (!data.tanggal) { toast.info("Belum ada data sebelumnya untuk cabang ini"); return; }
      const newDrafts = emptyDrafts();
      let total = 0;
      for (const cat of CATS) {
        const rows = data.data?.[cat.key] || [];
        newDrafts[cat.key] = rows.map((r) => {
          const cleaned = {};
          for (const col of cat.columns) cleaned[col.name] = r[col.name] ?? (col.type === "number" ? 0 : "");
          return cleaned;
        });
        total += rows.length;
      }
      setDrafts(newDrafts);
      toast.success(`${total} baris disalin dari ${data.tanggal} — periksa & klik Simpan Semua`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyalin");
    } finally { setCopying(false); }
  };
  const exportPDF = async () => {
    if (!cabangId || !tanggal) { toast.error("Pilih cabang & tanggal dulu"); return; }
    setExporting(true);
    try {
      const params = { period: "daily", tanggal_from: tanggal, tanggal_to: tanggal, cabang_id: cabangId };
      if (isSuper && adminId) params.admin_id = adminId;
      const { data } = await api.get("/dashboard/summary", { params });
      const usahaLabel = isSuper ? (admins.find((a) => a.id === adminId)?.name || "—") : (user?.name || "—");
      const cabangLabel = cabangList.find((c) => c.id === cabangId)?.name || "—";
      generateReportPDF({
        usahaLabel, cabangLabel, periodType: "daily",
        periodRangeLabel: tanggal, data,
        detail: existing || undefined,
        filename: `InputHarian_${usahaLabel.replace(/\s+/g, "_")}_${cabangLabel.replace(/\s+/g, "_")}_${tanggal}.pdf`,
      });
      toast.success("PDF berhasil diunduh");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat laporan");
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={<span className="inline-flex items-center gap-2"><ClipboardEdit className="h-6 w-6 text-emerald-600" /> Input Data Harian</span>}
        subtitle="Pilih lokasi cabang dan tanggal — semua data yang Anda tambahkan/ubah otomatis terikat pada pilihan tersebut."
        actions={
          <div className="flex items-center gap-2">
            {!isViewer && (
              <Button variant="outline" onClick={copyFromLast} disabled={copying || !cabangId || !tanggal}
                      data-testid="copy-from-last-btn" className="border-slate-300">
                <Copy className="h-4 w-4 mr-2" /> {copying ? "Menyalin..." : "Salin Hari Sebelumnya"}
              </Button>
            )}
            {!isViewer && (
              <Button variant="outline" onClick={exportPDF} disabled={exporting || !cabangId || !tanggal}
                      data-testid="export-pdf-harian" className="border-slate-300">
                <FileDown className="h-4 w-4 mr-2" /> {exporting ? "Menyiapkan..." : "Unduh PDF"}
              </Button>
            )}
            {!isViewer && (
              <Button onClick={saveAll} disabled={saving || totalPending === 0}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-all-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : `Simpan Semua (${totalPending})`}
              </Button>
            )}
          </div>
        }
      />

      <Card className="p-5 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <div className={`grid grid-cols-1 ${isSuper ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 items-end`}>
          {isSuper && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Usaha</Label>
              <Select value={adminId} onValueChange={setAdminId}>
                <SelectTrigger data-testid="header-usaha" className="h-11 bg-white"><SelectValue placeholder="Pilih usaha dulu" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {admins.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Belum ada usaha. Buat akun Usaha di halaman Pengguna.</div>
                  ) : admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Lokasi Cabang</Label>
            <Select value={cabangId} onValueChange={setCabangId} disabled={isCabangUser || (isSuper && !adminId)}>
              <SelectTrigger data-testid="header-cabang" className="h-11 bg-white">
                <SelectValue placeholder={isSuper && !adminId ? "Pilih usaha dulu" : "Pilih lokasi cabang"} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {visibleCabang.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    {isSuper && !adminId ? "Pilih usaha terlebih dahulu" : "Belum ada cabang"}
                  </div>
                ) : visibleCabang.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700 inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Tanggal</Label>
            <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)}
                   disabled={isSuper && (!adminId || !cabangId)}
                   data-testid="header-tanggal" className="h-11 bg-white" />
          </div>
          <div className="text-sm text-slate-500 md:text-right md:pr-2">
            {loading ? "Memuat data..." :
              !cabangId ? <span className="text-slate-400">Lengkapi pilihan di atas</span> :
              existing && Object.values(existing).some((a) => a.length > 0)
                ? <span className="text-emerald-700">Data tersedia — klik ikon pensil untuk edit langsung</span>
                : <span>Belum ada data untuk cabang & tanggal ini</span>}
          </div>
        </div>
      </Card>

      {CATS.map((cat) => (
        <CategorySection
          key={cat.key}
          cat={cat}
          existingRows={existing?.[cat.key] || []}
          draftRows={drafts[cat.key]}
          edits={edits[cat.key]}
          onAddDraft={() => addDraftRow(cat.key)}
          onRemoveDraft={(idx) => removeDraftRow(cat.key, idx)}
          onUpdateDraft={(idx, field, val) => updateDraft(cat.key, idx, field, val)}
          onStartEdit={(row) => startEdit(cat.key, row)}
          onCancelEdit={(rowId) => cancelEdit(cat.key, rowId)}
          onUpdateEdit={(rowId, field, val) => updateEdit(cat.key, rowId, field, val)}
          onDeleteExisting={(id) => {
            const row = (existing?.[cat.key] || []).find((r) => r.id === id);
            if (row) deleteExisting(cat, row);
          }}
          disabled={!cabangId || !tanggal}
          readOnly={isViewer}
        />
      ))}

      {!isViewer && (
        <div className="flex justify-end">
          <Button onClick={saveAll} disabled={saving || totalPending === 0} size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-all-btn-bottom">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : `Simpan Semua (${totalPending})`}
          </Button>
        </div>
      )}
    </div>
  );
}

function CategorySection({ cat, existingRows, draftRows, edits, onAddDraft, onRemoveDraft, onUpdateDraft,
                           onStartEdit, onCancelEdit, onUpdateEdit, onDeleteExisting, disabled, readOnly }) {
  const editingCount = Object.keys(edits || {}).length;

  // Total footer: gabungan existing (pakai patch kalau sedang diedit) + drafts
  const allRows = [
    ...existingRows.map((r) => (edits?.[r.id] ? edits[r.id] : r)),
    ...draftRows,
  ];
  let footer = null;
  if (cat.selisih && allRows.length > 0) {
    const fromCol = cat.columns.find((c) => c.name === cat.selisih.from);
    const toCol = cat.columns.find((c) => c.name === cat.selisih.to);
    const totalFrom = allRows.reduce((s, r) => s + Number(r[cat.selisih.from] || 0), 0);
    const totalTo = allRows.reduce((s, r) => s + Number(r[cat.selisih.to] || 0), 0);
    footer = { items: [
      { label: `Total ${fromCol.label.replace(" (Rp)", "")}`, value: totalFrom, tone: "text-slate-800" },
      { label: `Total ${toCol.label.replace(" (Rp)", "")}`, value: totalTo, tone: "text-slate-800" },
      { label: "Total Selisih", value: totalFrom - totalTo, tone: "text-emerald-600" },
    ]};
  } else if (cat.key === "lainnya" && allRows.length > 0) {
    const total = allRows.reduce((s, r) => s + Number(r.pembelian || 0), 0);
    footer = { items: [{ label: "Total", value: total, tone: "text-rose-600" }] };
  }
  return (
    <Card className={`p-5 border-2 ${cat.accent}`} data-testid={`section-${cat.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading text-base font-bold tracking-tight text-slate-900">{cat.label}</div>
          <div className="text-xs text-slate-500">
            {existingRows.length} tersimpan
            {editingCount > 0 ? ` · ${editingCount} sedang diedit` : ""}
            {draftRows.length > 0 ? ` · ${draftRows.length} baris baru` : ""}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={onAddDraft} disabled={disabled || readOnly}
                data-testid={`add-row-${cat.key}`} className={`border-slate-300 ${readOnly ? "hidden" : ""}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Tambah Baris
        </Button>
      </div>

      {(existingRows.length > 0 || draftRows.length > 0) && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {cat.columns.map((c) => <TableHead key={c.name} className={c.w}>{c.label}</TableHead>)}
                {cat.selisih && <TableHead className="w-36 text-right">Selisih</TableHead>}
                {!readOnly && <TableHead className="w-24 text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody data-testid={`table-${cat.key}`}>
              {existingRows.map((row) => {
                const isEditing = !!edits?.[row.id];
                const patch = edits?.[row.id];
                const rowData = isEditing ? patch : row;
                const sel = selisihValue(cat, rowData);
                return (
                  <TableRow key={row.id} className={isEditing ? "" : "bg-slate-50/50"} data-testid={`existing-${cat.key}-${row.id}`}>
                    {cat.columns.map((c) => (
                      <TableCell key={c.name} className="text-sm align-middle">
                        {isEditing ? (
                          c.name === "catatan" ? (
                            <Textarea rows={1} value={patch[c.name] ?? ""}
                                      onChange={(e) => onUpdateEdit(row.id, c.name, e.target.value)}
                                      data-testid={`edit-${cat.key}-${row.id}-${c.name}`} className="min-h-9" />
                          ) : c.money ? (
                            <MoneyInput value={patch[c.name]}
                                        onChange={(v) => onUpdateEdit(row.id, c.name, v)}
                                        testid={`edit-${cat.key}-${row.id}-${c.name}`} required={c.required} />
                          ) : (
                            <Input type={c.type === "number" ? "number" : "text"} value={patch[c.name] ?? ""}
                                   onChange={(e) => onUpdateEdit(row.id, c.name, e.target.value)}
                                   data-testid={`edit-${cat.key}-${row.id}-${c.name}`} className="h-9" required={c.required} />
                          )
                        ) : (
                          c.money ? formatIDR(row[c.name]) : (c.type === "number" ? (row[c.name] ?? "—") : (row[c.name] || "—"))
                        )}
                      </TableCell>
                    ))}
                    {cat.selisih && (
                      <TableCell className={`text-right font-semibold ${sel >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                                 data-testid={`selisih-${cat.key}-${row.id}`}>
                        {formatIDR(sel)}
                      </TableCell>
                    )}
                    {!readOnly && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {isEditing ? (
                            <Button variant="ghost" size="icon" onClick={() => onCancelEdit(row.id)}
                                    data-testid={`cancel-edit-${cat.key}-${row.id}`} title="Batal edit">
                              <X className="h-4 w-4 text-slate-500" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" onClick={() => onStartEdit(row)}
                                    data-testid={`edit-existing-${cat.key}-${row.id}`} title="Edit">
                              <Pencil className="h-4 w-4 text-slate-700" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => onDeleteExisting(row.id)}
                                  data-testid={`delete-existing-${cat.key}-${row.id}`} title="Hapus">
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {draftRows.map((row, idx) => {
                const sel = selisihValue(cat, row);
                return (
                  <TableRow key={`draft-${idx}`} data-testid={`draft-${cat.key}-${idx}`}>
                    {cat.columns.map((c) => (
                      <TableCell key={c.name}>
                        {c.name === "catatan" ? (
                          <Textarea rows={1} value={row[c.name] ?? ""}
                                    onChange={(e) => onUpdateDraft(idx, c.name, e.target.value)}
                                    data-testid={`draft-${cat.key}-${idx}-${c.name}`} className="min-h-9" />
                        ) : c.money ? (
                          <MoneyInput value={row[c.name]}
                                      onChange={(v) => onUpdateDraft(idx, c.name, v)}
                                      testid={`draft-${cat.key}-${idx}-${c.name}`} required={c.required} />
                        ) : (c.name === "item" || c.name === "nama") ? (
                          <>
                            <Input type="text" value={row[c.name] ?? ""}
                                   list={`suggest-${cat.key}-${c.name}`}
                                   onChange={(e) => onUpdateDraft(idx, c.name, e.target.value)}
                                   data-testid={`draft-${cat.key}-${idx}-${c.name}`}
                                   className="h-9" required={c.required} />
                            <ItemDatalist id={`suggest-${cat.key}-${c.name}`} kategori={cat.key} />
                          </>
                        ) : (
                          <Input type={c.type === "number" ? "number" : "text"} value={row[c.name] ?? ""}
                                 onChange={(e) => onUpdateDraft(idx, c.name, e.target.value)}
                                 data-testid={`draft-${cat.key}-${idx}-${c.name}`} className="h-9" required={c.required} />
                        )}
                      </TableCell>
                    ))}
                    {cat.selisih && (
                      <TableCell className={`text-right font-semibold ${sel >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                                 data-testid={`draft-selisih-${cat.key}-${idx}`}>
                        {formatIDR(sel)}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => onRemoveDraft(idx)}
                              data-testid={`remove-draft-${cat.key}-${idx}`}>
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-1.5 px-4 py-2.5 bg-slate-50 border-t border-slate-200"
                 data-testid={`footer-total-${cat.key}`}>
              {footer.items.map((f, i) => (
                <div key={i} className="inline-flex items-center gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
                  <span className={`font-mono text-sm font-bold ${f.tone}`}>{formatIDR(f.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {existingRows.length === 0 && draftRows.length === 0 && (
        <div className="text-center py-6 text-sm text-slate-400">
          {readOnly ? "Belum ada data pada tanggal ini." : <>Belum ada data. Klik <b>Tambah Baris</b> untuk mulai mengisi.</>}
        </div>
      )}
    </Card>
  );
}
